import React, { useState, useEffect, useRef } from "react";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";
import StageColumn from "../../../../../component/board/stagecolumn";
import { useApiCall } from "../../../../../component/custom-hooks/api-call";
import { Alert, Snackbar } from "@mui/material";
import { useMemo } from "react";
import DealQuickView from "../popup";
import InnerLoader from "../../../../../component/ui/loader/inner-loader";
import WonDeal from "../won-deal/won-form-deal";
import LostDeal from "../lost-deal/lost-form-deal";

export default function DealBoard({
  DealStages = [],
  filters,
  groupBy = "dealstatus",
  sendFilters,
  PackageList = [],
  ProductList = [],
  ClientList = [],
  UserList = [],
  contactId = {},
  onDealUpdated = () => {}, // refresh after update
  onLoadMoreClients = {},
  hasMoreClients = {},
  page = {},
  isLoadingClients = {},
}) {
  const [deals, setDeals] = useState([]);
  const [stages, setStages] = useState([]);
  const [stageMeta, setStageMeta] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [isDealModalOpen, setIsDealModalOpen] = useState(false);
  const [wonFormDeal, setWonFormDeal] = useState(null);
  const [lostFormDeal, setLostFormDeal] = useState(null);
  const [pagination, setPagination] = useState({});
  const [isApiPending, setIsApiPending] = useState(false);
  const [cardLoad, setCardload] = useState(false);
  const [activeCardId, setActiveCardId] = useState(null);

  useEffect(() => {
    const handleClickOutside = () => {
      setActiveCardId(null);
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const [wonFormData, setWonFormData] = useState({
    transaction_key: "",
    payment_method: "",
    transaction_date: "",
    transaction_time: "",
  });
  const [alertData, setAlertData] = useState({
    isShow: false,
    type: "success",
    message: "",
  });
  const handleCloseAlert = () => {
    setAlertData({ isShow: false, type: "", message: "" });
  };
  const apiCall = useApiCall();

  useEffect(() => {
    setIsLoading(true);
    setDeals([]);
    setPagination({});
    loadDeals(1);
    isApiPendingRef.current = true;
    // loadStages();
  }, [filters, groupBy]);

  const loadDeals = async (page = 1) => {
    console.log("api Called loadDeals", page, groupBy);
    let extraUrl = `?group_by=${groupBy}&page=${page}`;
    if (filters?.length) {
      for (let i = 0; i < filters.length; i++) {
        if (
          filters[i]?.value !== undefined &&
          filters[i]?.value !== null &&
          filters[i]?.value !== ""
        ) {
          extraUrl += `&${filters[i].name}=${filters[i].value}`;
        }
      }
    }

    setIsApiPending(true);

    try {
      const res = await apiCall({
        method: "get",
        url: `deals/deals-kanban${extraUrl}`,
      });

      sendFilters(res?.custom_filters);
      isApiPendingRef.current = false;

      const stageMeta = {};
      const dynamicStages = res.kanban.map((bucket) => ({
        id: bucket.bucket,
        name: bucket.bucket.replace(/_/g, " "),
        total_count: bucket.total_count,
        total_amount: bucket.total_amount,
      }));

      if (page === 1) {
        // fresh load
        setStages(dynamicStages);
      } else {
        // append only new stages (avoid duplicates)
        setStages((prevStages) => {
          const existingIds = new Set(prevStages.map((s) => s.id));
          const uniqueNew = dynamicStages.filter((s) => !existingIds.has(s.id));
          return [...prevStages, ...uniqueNew];
        });
      }

      if (res.status) {
        const kanbanDeals = res.kanban.flatMap((bucket) =>
          bucket.deals.map((deal, index) => {
            const metaMap = Object.fromEntries(
              (deal.metas || []).map((meta) => [
                meta.meta_key,
                meta.meta_value,
              ]),
            );

            return {
              id: deal.dealid.toString(),
              name: deal.name,
              deal_product: deal.deal_product,
              deal_package: deal.deal_package_name,
              deal_value: Number(metaMap.dealamount || 0),
              current_stage: bucket.bucket,
              order: index,
              contact_name: deal.contact?.name,
              contact_email: deal.contact?.email || "",
              contact_phone: deal.contact?.phone || "",
              company_name: deal.contact?.companyname || "",
              source: deal.contact?.source || "",
              deal_date: deal.dealtime,
              assigned_to: deal.made_by?.name || "",
              priority: "",
              is_closed: deal.is_closed === 1,
              dealkey: deal.dealkey,
              completeDeal: deal,
            };
          }),
        );

        setDeals((prev) =>
          page === 1 ? kanbanDeals : [...prev, ...kanbanDeals],
        );
        setPagination(res.pagination);
      }
    } catch (error) {
      console.error("Error loading deals:", error);
    }
    setIsLoading(false);
  };

  const handleDealClick = (deal) => {
    setSelectedDeal(deal);
    setIsDealModalOpen(true);
  };

  const handleDragEnd = async (result) => {
    setActiveCardId(null);
    if (!result.destination) return;

    const { source, destination, draggableId } = result;

    const deal = deals.find((d) => d.id === draggableId);
    if (!deal) return;

    const newStage = destination.droppableId;

    if (source.droppableId === destination.droppableId) {
      const columnDeals = deals
        .filter((d) => d.current_stage === source.droppableId)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      const [movedDeal] = columnDeals.splice(source.index, 1);
      columnDeals.splice(destination.index, 0, movedDeal);

      const newDeals = deals.map((d) => {
        const updated = columnDeals.find((cd) => cd.id === d.id);
        if (updated)
          return { ...d, order: columnDeals.findIndex((cd) => cd.id === d.id) };
        return d;
      });

      setDeals(newDeals);
      return;
    }

    // temporarily update UI
    setDeals((prev) =>
      prev.map((d) =>
        d.id === draggableId ? { ...d, current_stage: newStage } : d,
      ),
    );

    if (newStage === "Won") {
      // Open custom form modal instead of SweetAlert2
      setWonFormDeal(deal);

      // temporarily update stage in UI (optional)
      setDeals((prev) =>
        prev.map((d) =>
          d.id === deal.id ? { ...d, current_stage: newStage } : d,
        ),
      );

      return; // stop further SweetAlert2 logic
    }

    if (newStage === "Lost") {
      setLostFormDeal(deal);
      setDeals((prev) =>
        prev.map((d) =>
          d.id === deal.id ? { ...d, current_stage: newStage } : d,
        ),
      );
      return;
    }

    // SweetAlert2 modal
    const resultSwal = await Swal.fire({
      title: `Move deal "${deal.name}"?`,
      text: `Move from "${deal.current_stage}" to "${newStage}"?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      reverseButtons: true,
    });

    if (!resultSwal.isConfirmed) {
      // revert if cancelled
      setDeals((prev) =>
        prev.map((d) =>
          d.id === deal.id ? { ...d, current_stage: deal.current_stage } : d,
        ),
      );
      return;
    }

    // call API
    try {
      await apiCall({
        method: "post",
        url: "deals/update-deal-status",
        data: {
          dealkey: deal.dealkey,
          dealstatus: newStage.toLowerCase().replace(/\s+/g, "_"),
        },
      });

      setAlertData({
        isShow: true,
        type: "success",
        message: "Deal moved successfully!",
      });
    } catch (err) {
      console.error("Status update error:", err);
      // revert UI if API fails
      setDeals((prev) =>
        prev.map((d) =>
          d.id === deal.id ? { ...d, current_stage: deal.current_stage } : d,
        ),
      );

      setAlertData({
        isShow: true,
        type: "error",
        message: "Failed to update deal status",
      });
    }
  };

  const totalPipelineDeals = deals.length;

  const filteredDeals = useMemo(
    () =>
      deals.filter(
        (d) =>
          d.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          d.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [deals, searchTerm],
  );

  const getDealsByStage = (stageId) =>
    filteredDeals
      .filter((d) => d.current_stage === stageId)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const getTotalValue = () =>
    deals.reduce((sum, d) => sum + (d.deal_value || 0), 0);
  const getWonDealsValue = () =>
    deals
      .filter((d) => d.current_stage === "Won")
      .reduce((sum, d) => sum + (d.deal_value || 0), 0);

  /// Scroll Pagination ///

  const [scrollPosition, setScrollPosition] = useState(0);
  const divRef = useRef(null); // Create a ref for the div

  const isApiPendingRef = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      if (divRef.current) {
        const { scrollTop, clientHeight, scrollHeight } = divRef.current;

        if (scrollTop + clientHeight >= scrollHeight - 180) {
          if (
            !isApiPendingRef.current &&
            pagination?.current_page != pagination?.last_page
          ) {
            isApiPendingRef.current = true; // lock immediately
            setIsApiPending(true);
            setCardload(true);

            loadDeals(pagination?.current_page + 1).finally(() => {
              isApiPendingRef.current = false; // release after API finishes
              setIsApiPending(false);
              setCardload(false);
            });
          }
        }
      }
    };

    const divElement = divRef.current;
    if (divElement) divElement.addEventListener("scroll", handleScroll);

    return () => {
      if (divElement) divElement.removeEventListener("scroll", handleScroll);
    };
  }, [pagination]);

  const onSuccess = (message) => {
    setAlertData({
      isShow: true,
      type: "success",
      message: "Deal marked as Won!",
    });

    // update UI
    setDeals((prev) =>
      prev.map((d) =>
        d.id === wonFormDeal.id ? { ...d, current_stage: "Won" } : d,
      ),
    );

    setWonFormDeal(null);

    setIsDealModalOpen(false);
    setSelectedDeal({});
  };

  const onError = (message) => {
    setAlertData({
      isShow: true,
      type: "error",
      message: "Failed to mark deal as Won",
    });

    setDeals((prev) =>
      prev.map((d) =>
        d.id === wonFormDeal.id
          ? { ...d, current_stage: wonFormDeal.current_stage }
          : d,
      ),
    );

    setWonFormDeal(null);
  };

  const QuickSuccess = (message) => {
    setAlertData({ isShow: true, type: "success", message: message });
    setIsDealModalOpen(false);
    setSelectedDeal({});
  };

  const QuickError = (message) => {
    setAlertData({ isShow: true, type: "error", message: message });
  };

  const onSuccess2 = (message) => {
    setAlertData({ isShow: true, type: "success", message: message });
    loadDeals(1);
  };

  const onError2 = (message) => {
    setAlertData({ isShow: true, type: "error", message: message });
  };

  const onLostSuccess = (message) => {
    setAlertData({
      isShow: true,
      type: "success",
      message: "Deal marked as Lost!",
    });

    setDeals((prev) =>
      prev.map((d) =>
        d.id === lostFormDeal.id ? { ...d, current_stage: "Lost" } : d,
      ),
    );

    setLostFormDeal(null);
  };

  const onLostError = (message) => {
    setAlertData({
      isShow: true,
      type: "error",
      message: "Failed to mark deal as Lost",
    });

    setDeals((prev) =>
      prev.map((d) =>
        d.id === lostFormDeal.id
          ? { ...d, current_stage: lostFormDeal.current_stage }
          : d,
      ),
    );

    setLostFormDeal(null);
  };

  const handleClose = () => {
    setAlertData({ isShow: false });
  };

  const boxColor = {
    just_leads: { color: "#9D5FEF" },
    leads_with_deal: { color: "#5FE0EF" },
    lead_today_payment: { color: "#98ADFF" },
    contact_overdue_payment: { color: "#60E442" },
    not_contacted_2_days: { color: "#E9E03F" },
    untouched_leads: { color: "#335CFF" },
  };

  return (
    <>
      {isLoading ? (
        <InnerLoader />
      ) : (
        <div className="py-6 px-[15px] rounded-[10px]  border-[#e1E4Ea]  bg-[#fff]  h-full w-full overflow-hidden">
          <div
            ref={divRef}
            className="flex flex-col pb-2  overflow-auto "
            style={{ height: "calc(100%)" }}
          >
            <div className="flex-1">
              <DragDropContext
                onDragEnd={groupBy === "dealstatus" ? handleDragEnd : undefined}
              >
                <div className="flex gap-6 h-full ">
                  <AnimatePresence>
                    {stages.map((stage, index) => (
                      <motion.div
                        key={stage.id}
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="w-80 flex-shrink-0"
                      >
                        <Droppable droppableId={stage.id}>
                          {(provided, snapshot) => (
                            <StageColumn
                              stage={stage}
                              deals={getDealsByStage(stage.id)}
                              provided={provided}
                              onDealClick={handleDealClick}
                              isLoading={isLoading}
                              totalPipelineDeals={totalPipelineDeals}
                              dragEnabled={groupBy === "dealstatus"}
                              colors={boxColor[stage.id]}
                              isLast={stages?.length - 1 === index}
                              cardLoad={cardLoad}
                              DealStages={DealStages}
                              PackageList={PackageList}
                              ProductList={ProductList}
                              ClientList={ClientList}
                              UserList={UserList}
                              contactId={contactId}
                              onDealUpdated={onDealUpdated} // refresh after update
                              onSubmit={onSuccess2}
                              onError={onError2}
                              onLoadMoreClients={onLoadMoreClients}
                              hasMoreClients={hasMoreClients}
                              page={page}
                              isLoadingClients={isLoadingClients}
                              activeCardId={activeCardId}
                              setActiveCardId={setActiveCardId}
                            />
                          )}
                        </Droppable>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </DragDropContext>

              {isDealModalOpen && (
                <DealQuickView
                  onClose={() => {
                    setIsDealModalOpen(false);
                    setSelectedDeal({});
                  }}
                  data={{ ...selectedDeal, dealid: selectedDeal?.id }}
                  DealStages={DealStages}
                  onError={QuickError}
                  onSuccess={QuickSuccess}
                />
              )}

              {wonFormDeal && (
                <WonDeal
                  wonFormDeal={wonFormDeal}
                  onClose={() => {
                    setWonFormDeal(null);
                    setDeals((prev) =>
                      prev.map((d) =>
                        d.id === wonFormDeal.id
                          ? { ...d, current_stage: wonFormDeal.current_stage }
                          : d,
                      ),
                    );
                  }}
                  onError={onError}
                  onSubmit={onSuccess}
                />
              )}

              {lostFormDeal && (
                <LostDeal
                  lostFormDeal={lostFormDeal}
                  onClose={() => {
                    setLostFormDeal(null);
                    setDeals((prev) =>
                      prev.map((d) =>
                        d.id === lostFormDeal.id
                          ? { ...d, current_stage: lostFormDeal.current_stage }
                          : d,
                      ),
                    );
                  }}
                  onError={onLostError}
                  onSubmit={onLostSuccess}
                />
              )}
            </div>

            <Snackbar
              open={alertData.isShow}
              autoHideDuration={3000}
              onClose={handleCloseAlert}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              <Alert
                onClose={handleCloseAlert}
                severity={alertData.type}
                sx={{ width: "100%" }}
              >
                {alertData.message}
              </Alert>
            </Snackbar>

            <Snackbar
              open={alertData?.isShow}
              autoHideDuration={3000}
              onClose={handleClose}
            >
              <Alert
                onClose={handleClose}
                severity={alertData?.type}
                variant="filled"
                sx={{ width: "100%" }}
              >
                {alertData?.message}
              </Alert>
            </Snackbar>
          </div>
        </div>
      )}
    </>
  );
}
